"use client"

import * as React from "react"
import { useForm, Controller } from "react-hook-form"
import { zodFormResolver } from "@/util/zod-form-resolver"
import { z } from "zod"

import type { GoogleAddressData } from "./place-input"

import { EMAIL_STORAGE } from "../../constants/storage"
import { generateFormQueryParams } from "../../services/links"

import { useGoogleMaps } from "../../app/hooks/use-google-maps"

import { PlaceInput } from "./place-input"
import { Email } from "./email"
import { env } from "../../config"

const addressSchema = z.object({
  formatted_address: z.string(),
  city: z.string(),
  state_code: z.string(),
  lat: z.number(),
  lng: z.number(),
  zip: z.string().nullable(),
})

const transportationSchema = z.object({
  pickup: addressSchema.nullable(),
  delivery: addressSchema.nullable(),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
})

export const PetBreedHeroForm: React.FC<{
  hasDelivery?: boolean
  hasPickup?: boolean
}> = ({ hasPickup = true, hasDelivery = true }) => {
  const APP_HOST = env("APP_HOST")

  const { handleSubmit, control } = useForm<{
    pickup: null | GoogleAddressData
    delivery: null | GoogleAddressData
    email: string
  }>({
    defaultValues: {
      pickup: null,
      delivery: null,
      email: "",
    },
    resolver: zodFormResolver(transportationSchema),
  })

  const { mapsLoaded } = useGoogleMaps()

  const onSubmit = handleSubmit((data) => {
    let queryParams: Record<string, string | number> = {}

    if (data.pickup != null) {
      queryParams.pickupFA = data.pickup.formatted_address
      queryParams.pickupSC = data.pickup.state_code
      queryParams.pickupCt = data.pickup.city
      queryParams.pickupLat = data.pickup.lat
      queryParams.pickupLng = data.pickup.lng
      queryParams.pickupZip = data.pickup.zip ?? ""
    }

    if (data.delivery != null) {
      queryParams.deliveryFA = data.delivery.formatted_address
      queryParams.deliverySC = data.delivery.state_code
      queryParams.deliveryCt = data.delivery.city
      queryParams.deliveryLat = data.delivery.lat
      queryParams.deliveryLng = data.delivery.lng
      queryParams.deliveryZip = data.delivery.zip ?? ""
    }

    if (data.email) {
      queryParams.email = data.email
      sessionStorage.setItem(EMAIL_STORAGE, data.email)
    }

    const formQueryParams = generateFormQueryParams({
      query: queryParams,
    })

    window.location.assign(`${APP_HOST}/shipments/new${formQueryParams}`)
  })

  return (
    <form className="grid grid-cols-1 gap-y-4" onSubmit={onSubmit}>
      {hasPickup && (
        <div className="mb-0 pb-0">
          <Controller
            name="pickup"
            control={control}
            render={({ field, fieldState }) => {
              const error = fieldState.error
                ? fieldState.error.message
                : undefined

              return (
                <PlaceInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Pickup location"
                  type="pickup"
                  error={error}
                  id="pickup-location"
                  ref={field.ref}
                  mapsLoaded={mapsLoaded}
                />
              )
            }}
          />
        </div>
      )}
      {hasDelivery && (
        <div>
          <Controller
            name="delivery"
            control={control}
            render={({ field, fieldState }) => {
              const error = fieldState.error
                ? fieldState.error.message
                : undefined

              return (
                <PlaceInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Delivery location"
                  type="dropoff"
                  error={error}
                  id="dropoff-location"
                  ref={field.ref}
                  mapsLoaded={mapsLoaded}
                />
              )
            }}
          />
        </div>
      )}
      <div>
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => {
            const error = fieldState.error
              ? fieldState.error.message
              : undefined

            return (
              <div>
                <Email
                  value={field.value}
                  ref={field.ref}
                  onChange={field.onChange}
                  label="Email"
                  error={error}
                  id="email"
                />
              </div>
            )
          }}
        />
      </div>

      <div>
        <span className="text-center block pb-6 text-sm">
          We'll keep your email secure. Unsubscribe at anytime.
        </span>
        <button
          type="submit"
          onClick={onSubmit}
          className="w-full py-5 rounded text-center bg-blue7 hover:bg-blue-700 text-white text-md"
        >
          Get Free Estimates
        </button>
      </div>
    </form>
  )
}
